import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileText, Download, Eye, Lock, Star, Search } from 'lucide-react';
import { Empty, PageHeader, PageLoader, fmtDate, fmtSize } from '../../components/ui';
import api, { downloadFile, openFile, blobErrMsg } from '../../services/api';
import { useMember } from '../../context/AuthContext';

export default function Documents() {
  const { member } = useMember();
  const [categories, setCategories] = useState(null);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState('all');

  useEffect(() => {
    if (member.status !== 'ACTIVE') return;
    api.get('/member/documents').then(r => setCategories(r.data)).catch(() => setCategories([]));
  }, [member.status]);

  if (member.status !== 'ACTIVE') {
    return (
      <div className="card mx-auto max-w-lg p-8 text-center">
        <Lock size={40} className="mx-auto text-gold-600" />
        <h1 className="mt-3 font-display text-2xl font-bold text-navy-900">Members only</h1>
        <p className="mt-2 text-sm text-gray-600">Verify your email address to unlock the Group documents library.</p>
        <Link to="/verify-email" className="btn-primary mt-5">Verify my email</Link>
      </div>
    );
  }
  if (!categories) return <PageLoader />;

  const q = search.trim().toLowerCase();
  const shown = categories
    .filter(c => active === 'all' || c.id === active)
    .map(c => ({ ...c, documents: c.documents.filter(d => !q || d.title.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q)) }))
    .filter(c => c.documents.length);

  const act = async (doc, inline) => {
    try {
      if (inline) await openFile(`/member/documents/${doc.id}/download?inline=1`);
      else await downloadFile(`/member/documents/${doc.id}/download`, doc.originalName);
    } catch (err) {
      toast.error(await blobErrMsg(err, 'Could not open the document'));
    }
  };

  return (
    <div>
      <PageHeader title="Group documents" subtitle="Members-only library — reports, minutes, admissions information and official notices." />
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative mr-2 w-full sm:w-64">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input pl-9" placeholder="Search documents" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {[{ id: 'all', name: 'All' }, ...categories].map(c => (
          <button key={c.id} onClick={() => setActive(c.id)}
            className={`rounded-full px-3 py-1.5 text-sm ${active === c.id ? 'bg-navy-900 text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:ring-navy-300'}`}>
            {c.name}{c.documents && <span className="ml-1 opacity-60">{c.documents.length}</span>}
          </button>
        ))}
      </div>

      {!shown.length ? (
        <div className="card"><Empty icon={FileText} title="No documents yet">New documents published by the committee will appear here.</Empty></div>
      ) : shown.map(c => (
        <section key={c.id} className="mb-6">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">{c.name}</h2>
          <div className="card divide-y">
            {c.documents.map(d => (
              <div key={d.id} className="flex flex-wrap items-center gap-4 p-4">
                <span className="rounded-lg bg-navy-50 p-2.5 text-navy-700"><FileText size={20} /></span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 font-medium text-navy-900">
                    {d.title}{d.important && <span className="badge bg-gold-100 text-gold-800"><Star size={11} /> Important</span>}
                  </p>
                  {d.description && <p className="mt-0.5 text-sm text-gray-600">{d.description}</p>}
                  <p className="mt-0.5 text-xs text-gray-400">{fmtDate(d.publishedAt)} · {fmtSize(d.size)}</p>
                </div>
                <div className="flex gap-2">
                  {(d.mimeType === 'application/pdf' || d.mimeType.startsWith('image/')) && (
                    <button onClick={() => act(d, true)} className="btn-outline btn-sm"><Eye size={14} />View</button>
                  )}
                  <button onClick={() => act(d, false)} className="btn-primary btn-sm"><Download size={14} />Download</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
